/**
 * RichOutputPanel - Panel displaying multiple rich outputs from code execution
 *
 * This component renders all rich outputs from a code execution result,
 * displaying charts, tables, and other content in a scrollable panel.
 *
 * Features:
 * - Shows count of outputs in header
 * - Renders each output using OutputRenderer
 * - Handles export actions (PNG download, CSV download)
 */

import { useCallback } from 'react';
import type { RichOutput } from '@deep-agents/shared-types';
import { OutputRenderer } from './OutputRenderer';

// ============================================================================
// TYPES
// ============================================================================

interface RichOutputPanelProps {
  /** Array of rich outputs to display */
  outputs: RichOutput[];
  /** Optional additional CSS classes */
  className?: string;
}

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * RichOutputPanel Component
 *
 * Displays a list of rich outputs with export functionality.
 * Each output is rendered using the OutputRenderer component.
 *
 * Export handling:
 * - PNG: Creates a download link with base64 data URL
 * - CSV: Creates a Blob and temporary object URL for download
 */
export function RichOutputPanel({ outputs, className = '' }: RichOutputPanelProps) {
  // ==========================================================================
  // EXPORT HANDLER
  // ==========================================================================

  /**
   * Handle export requests from OutputRenderer components
   *
   * @param type - Export type ('png' for images, 'csv' for tables)
   * @param data - The data to export (base64 string for PNG, CSV string for tables)
   */
  const handleExport = useCallback((type: 'png' | 'csv', data: string) => {
    if (type === 'png') {
      // Download as PNG image
      // Create a temporary link element with the base64 data URL
      const link = document.createElement('a');
      link.href = `data:image/png;base64,${data}`;
      link.download = `chart-${Date.now()}.png`; // Unique filename with timestamp
      link.click();
    } else if (type === 'csv') {
      // Download as CSV file
      // Create a Blob from the CSV data and generate an object URL
      const blob = new Blob([data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);

      // Create a temporary link element
      const link = document.createElement('a');
      link.href = url;
      link.download = `data-${Date.now()}.csv`; // Unique filename with timestamp
      link.click();

      // Clean up the object URL to free memory
      URL.revokeObjectURL(url);
    }
  }, []);

  // ==========================================================================
  // EMPTY STATE
  // ==========================================================================

  // Don't render anything if there are no outputs
  if (outputs.length === 0) {
    return null;
  }

  // ==========================================================================
  // RENDER
  // ==========================================================================

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Header with output count */}
      <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">
        Results ({outputs.length})
      </div>

      {/* List of outputs */}
      <div className="space-y-3">
        {outputs.map((output, index) => (
          <OutputRenderer
            key={index}
            output={output}
            onExport={handleExport}
          />
        ))}
      </div>
    </div>
  );
}
